import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import ScrollUpButtonContainer from '../Shared/ScrollUpButton/ScrollUpButtonContainer';
import { getSiteUrl, getTradeDeskIframe } from '@utils/config';
import Button from '@components/Shared/Buttons/Button';
import Modal from '@components/Shared/Modal/Modal';
import PhoneIcon from 'pubweb-smokey/dist/images/svg/iconography-16x16/phone.svg';
import LocationIcon from 'pubweb-smokey/dist/images/svg/iconography-16x16/location-line.svg';
import { getStateSlug, getFullState } from '@utils/stateUtils';
import {
  getDealerLandingPageInfo,
  getDealerAvailableModels,
  getDealerInventoryModels,
} from '@services/dealerService';
import {
  formatDealerName,
  formatPhone,
  formatStringToDisplayCase,
  formatNumberWithCommas,
} from 'pubweb-smokey/dist/utils/formatters';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import { exportUTMParametersFromSessionCache } from 'pubweb-smokey/dist/utils/utils';
import { getLinkWithUTMParameters } from '@utils/utils';
import HomeCardDetailed from '@components/Shared/HomeCards/Detailed/HomeCardDetailed';
import { DealerLPStyles } from './DealerLP.styled';
import AreaHeader from '../Shared/AreaHeader/AreaHeader';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { DXAccountContext } from '@contexts/DXAccountContext';
import { LocationsContext } from '@contexts/LocationsContext';
import { getWebsiteAreaContent } from '@services/contentfulService';
import {
  renderWebsiteAreaContent,
  renderWebForm,
} from '@utils/WebsiteAreaContent';
import DealerLPFormSuccessSVG from './DealerLPFormSuccessSVG';
import { pushGtmFormAction } from '@utils/gtmUtils';

const websiteAreaDealerLandingPageContentfulKey = '1HtkkneAs8Vg2QtA90f9eY'; //DLP WAC

const modelsPerPage = 6;

const DealerLP = ({
  ssrDealerInfo,
  ssrAvailableModels,
  ssrInventoryModels,
  dealerSlug,
  ssrWebsiteAreaContent,
}) => {
  const locationsContext = useContext(LocationsContext);
  const accountContext = useContext(DXAccountContext);
  const favoriteContext = useContext(DXFavoritesContext);

  const getFormData = () => {
    let postalCode = locationsContext.state.postalCode || '';

    if (accountContext.state.account?.postalCode) {
      postalCode = accountContext.state.account.postalCode; // eslint-disable-line prefer-destructuring
    }

    const newFormData = {
      firstName: accountContext.state.account?.firstName || '',
      lastName: accountContext.state.account?.lastName || '',
      email: accountContext.state.email || '',
      zip: postalCode,
      phone: accountContext.state.account?.phone || '',
    };

    return newFormData;
  };

  const [showHours, setShowHours] = useState(false);
  const [dealerInfo, setDealerInfo] = useState(ssrDealerInfo);
  const [formData, setFormData] = useState(getFormData());
  const [websiteAreaContent, setWebsiteAreaContent] = useState(
    ssrWebsiteAreaContent
  );
  const [dealerModels, setDealerModels] = useState(null);
  const [inventoryModels, setInventoryModels] = useState(
    ssrInventoryModels || []
  );
  const [availableModels, setAvailableModels] = useState(
    ssrAvailableModels || []
  );
  const [modelsDisplayed, setModelsDisplayed] = useState(modelsPerPage);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showContactUsFormModal, setShowContactUsFormModal] = useState(false);
  const [showVirtualTourModal, setShowVirtualTourModal] = useState(false);
  const [virtualTourDisplayed, setVirtualTourDisplayed] = useState(null);

  const [modelInfo, setModelInfo] = useState(null);

  const locationsUrl =
    locationsContext.state.postalCode !== null
      ? `/locations/?postalCode=${locationsContext.state.postalCode}&distance=${locationsContext.state.distance}`
      : `/locations/`;

  //One the client-side, cache any utm parameters on this page.  Append to dealer website link.
  const [browserCacheUTM, setBrowserCacheUTM] = useState('');
  useEffect(() => {
    setBrowserCacheUTM(exportUTMParametersFromSessionCache(true));
  }, []);

  useEffect(() => {
    if (!dealerInfo) {
      getDealerLandingPageInfo(dealerSlug).then((result) => {
        setDealerInfo(result);
      });
    }

    if (inventoryModels && availableModels) {
      mergeModels(inventoryModels, availableModels);
    }

    if (!inventoryModels) {
      getDealerAvailableModels({ dealerSlug: dealerSlug }).then((result) => {
        setAvailableModels(result);
        mergeModels(inventoryModels, result);
      });
    }

    if (!availableModels) {
      getDealerInventoryModels({ dealerSlug: dealerSlug }).then((result) => {
        setInventoryModels(result);
        mergeModels(result, availableModels);
      });
    }

    if (!websiteAreaContent) {
      getWebsiteAreaContent(websiteAreaDealerLandingPageContentfulKey).then(
        (result) => {
          setWebsiteAreaContent(result);
        }
      );
    }
  }, []);

  const mergeModels = (inventory, available) => {
    setDealerModels([...inventory, ...available]);
  };

  useEffect(() => {
    setFormData(getFormData());
  }, [accountContext.state.account, locationsContext.state.postalCode]);

  useEffect(() => {
    if (showFormModal) {
      document.getElementById('firstNameModal').focus();
    }
  }, [showFormModal]);

  const getHoursDisplay = () => {
    const weekDayHours = dealerInfo.dealer.weekdayHours || '';
    const fridayHours = dealerInfo.dealer.fridayHours || '';
    const saturdayHours = dealerInfo.dealer.saturdayHours || '';
    const sundayHours = dealerInfo.dealer.sundayHours || '';

    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu'];
    const hoursDisplay = [];

    for (let i = 0; i < 4; i++) {
      hoursDisplay.push(
        <div key={'weekday-' + weekDays[i]}>
          <p className="caption">{weekDays[i]}:</p>
          <p className="caption">{weekDayHours}</p>
        </div>
      );
    }

    hoursDisplay.push(
      <div key="weekday-Fri">
        <p className="caption">Fri:</p>
        <p className="caption">{fridayHours}</p>
      </div>
    );

    hoursDisplay.push(
      <div key="weekday-Sat">
        <p className="caption">Sat:</p>
        <p className="caption">{saturdayHours}</p>
      </div>
    );

    hoursDisplay.push(
      <div key="weekday-Sun">
        <p className="caption">Sun:</p>
        <p className="caption">{sundayHours}</p>
      </div>
    );

    return hoursDisplay;
  };

  const extData = [
    {
      dealerNumber: dealerInfo.dealer.dealerNumber,
      modelNumber: modelInfo !== null ? modelInfo.modelNumber : null,
    },
  ];

  const portalLogin = () => {
    setShowFormModal(false);
    accountContext.actions.navigateToPortal('/portal/');
  };

  const viewInFavorites = () => {
    setShowFormModal(false);
    accountContext.actions.navigateToPortal('/portal/favorites/');
  };

  const addToFavorites = () => {
    setShowFormModal(false);
    favoriteContext.actions.toggleFavoriteModel(modelInfo?.modelNumber);
    setTimeout(() => {
      accountContext.actions.navigateToPortal('/portal/favorites/');
    }, 1000);
  };

  const thankYouInfo = {
    thankYouIncludeHeader: false,
    thankYouMessageText: (
      <>
        Thank you for reaching out. A home center consultant will be in touch
        shortly.
        {getTradeDeskIframe()}
      </>
    ),
    thankYouButtonText: accountContext.state?.account
      ? modelInfo &&
        !!favoriteContext.state.favoriteModels?.filter(
          (faveModels) => faveModels.modelNumber === modelInfo.modelNumber
        )[0]
        ? 'View In Favorites'
        : 'Add to Favorites ❤'
      : 'Log In',
    thankYouButtonAutomationId: 'dlp-thank-you-click',
    thankYouImage: <DealerLPFormSuccessSVG />,
    thankYouButtonAction: () => {
      if (showFormModal) {
        if (accountContext.state?.account) {
          if (
            favoriteContext.state.favoriteModels?.filter(
              (faveModels) => faveModels.modelNumber === modelInfo.modelNumber
            )[0]
          ) {
            viewInFavorites();
          } else {
            addToFavorites();
          }
        } else {
          portalLogin();
        }
      }
    },
    isVisible: true,
    afterFormAction: () => {
      if (showFormModal) {
        setShowFormModal(false);
      }
    },
  };

  // The GA4 event for home center request info form. Needs a promise because webform processing expects it.
  const handleHCContactFormEvent = (name, buttonClicked) => {
    return new Promise((resolve) => {
      const buttonText =
        buttonClicked === '1'
          ? contentfulContactUsFormModalData?.firstFormButtonText
          : contentfulContactUsFormModalData?.secondFormButtonText;

      pushGtmFormAction({
        form_action_type: 'submit',
        hc_number: dealerInfo?.dealer?.dealerNumber,
        form_name: 'dealer_page',
        form_method: 'embedded',
        form_id: 'dealer_page_contact_us',
        form_submit_text: buttonText.trim().toLowerCase().replaceAll(' ', '_'),
      });
      resolve();
    });
  };

  // The GA4 event for (home center) request model info form. Needs a promise because webform processing expects it.
  const handleHCModelFormEvent = (name, buttonClicked) => {
    return new Promise((resolve) => {
      const buttonText =
        buttonClicked === '1'
          ? contentfulRequestFormModalData?.firstFormButtonText
          : contentfulRequestFormModalData?.secondFormButtonText;

      pushGtmFormAction({
        form_action_type: 'submit',
        model_number: modelInfo?.modelNumber,
        hc_number: dealerInfo?.dealer?.dealerNumber,
        form_name: 'dealer_page',
        form_method: 'embedded',
        form_id: 'dealer_page_model_info',
        form_submit_text: buttonText.trim().toLowerCase().replaceAll(' ', '_'),
      });
      resolve();
    });
  };

  // This is used for mobile and desktop content, but the 'contact us' and 'request home info' forms come from renderWebForm() in the code below. Except the desktop contact us form.
  // Since we will pass the proper GA event to renderWebForm functions below, we don't need to pass it here.
  let renderedAreaContent = renderWebsiteAreaContent(
    websiteAreaContent,
    extData,
    true,
    thankYouInfo,
    formData
  );

  const thankYouRequestInfo = {
    thankYouIncludeHeader: false,
    thankYouMessageText: (
      <>
        Thank you for reaching out. A home center consultant will be in touch
        shortly.
        {getTradeDeskIframe()}
      </>
    ),
    thankYouButtonText: 'Okay',
    thankYouButtonAutomationId: 'dlp-thank-you-click',
    thankYouImage: <DealerLPFormSuccessSVG />,
    thankYouButtonAction: () => {
      if (showFormModal) {
        setShowFormModal(false);
      }
    },
    isVisible: true,
    afterFormAction: () => {
      if (showFormModal) {
        setShowFormModal(false);
      }
    },
  };

  // This is only for the 'contact us' form on desktop.
  // Since we will directly use the renderWebForm content generated by the renderWebsiteAreaContent function, we need to pass the GA event we want to use here.
  let renderedRequestInfoContent = renderWebsiteAreaContent(
    websiteAreaContent,
    extData,
    true,
    thankYouRequestInfo,
    formData,
    handleHCContactFormEvent
  );

  //Deep cloning form data to use/edit on modal for individual models. This one is for Contact Us in mobile.
  let contentfulContactUsFormModalData =
    dealerInfo.dealer.brandAcronym === 'CLA'
      ? { ...renderedAreaContent.claytonRetailWebFormData }
      : dealerInfo.dealer.brandAcronym === 'IND'
        ? { ...renderedAreaContent.independentWebFormData }
        : { ...renderedAreaContent.familyOfBrandsWebFormData };
  let thankYouInfoMobile = { ...thankYouInfo };
  thankYouInfoMobile.thankYouButtonAction = () => {
    if (setShowContactUsFormModal) {
      setShowContactUsFormModal(false);
    }
  };

  //Request Info Form
  let contentfulRequestFormModalData =
    dealerInfo.dealer.brandAcronym === 'CLA'
      ? { ...renderedAreaContent.claytonRetailWebFormData }
      : dealerInfo.dealer.brandAcronym === 'IND'
        ? { ...renderedAreaContent.independentWebFormData }
        : { ...renderedAreaContent.familyOfBrandsWebFormData };
  let thankYouInfoModal = { ...thankYouInfo };

  //Changes for modal data.
  thankYouInfoModal.thankYouIncludeHeader = true;
  contentfulRequestFormModalData.disclaimerCheckbox = false;
  contentfulRequestFormModalData.originWebFormCategory = 'home-details';

  // Contact dealer form ('contact us')
  const contentfulContactUsMobileModalForm = renderWebForm(
    contentfulContactUsFormModalData,
    extData,
    true,
    thankYouInfoMobile,
    formData,
    'Mobile',
    handleHCContactFormEvent
  );

  // Request home model info form
  const contentfulRequestInfoModalForm = renderWebForm(
    contentfulRequestFormModalData,
    extData,
    true,
    thankYouInfoModal,
    formData,
    'Modal',
    handleHCModelFormEvent
  );

  const isSubdomain = (url) => {
    var regex = new RegExp(/^([a-z]+\:\/{2})?([\w-]+\.[\w-]+\.\w+)$/); // eslint-disable-line no-useless-escape
    return !!url.match(regex); // make sure it returns boolean
  };

  let dealerUrl = dealerInfo.dealer.url
    ? dealerInfo.dealer.url.toLowerCase()
    : null;
  if (dealerUrl) {
    //include a subdomain if missing
    dealerUrl = isSubdomain(dealerUrl)
      ? dealerUrl.trim()
      : 'www.' + dealerUrl.trim();
    //disable https for IND dealers
    dealerUrl =
      dealerInfo.dealer?.brandAcronym === 'IND'
        ? 'http://' + dealerUrl
        : 'https://' + dealerUrl;
    //append UTM parameters if available
    dealerUrl = getLinkWithUTMParameters(
      dealerUrl.endsWith('/') ? dealerUrl : (dealerUrl += '/'),
      browserCacheUTM
    );
  }

  return !dealerInfo ? null : (
    <DealerLPStyles className="dealer-landing-page">
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>
          {formatDealerName(dealerInfo.dealer.dealerName)} | Clayton Homes
        </title>
        <meta
          property="og:image"
          content={`${getSiteUrl('/public-assets/images/DealerLP-Logo.png')}`}
        />
        <meta
          name="description"
          content={`Visit ${formatDealerName(
            dealerInfo.dealer.dealerName
          )} and tour our quality-built, new mobile, modular and manufactured homes for sale available in your area.`}
        />
      </Head>

      <ScrollUpButtonContainer />

      <AreaHeader
        breadCrumbs={[
          {
            link: locationsUrl,
            text: 'Locations',
          },
          {
            text: getFullState(dealerInfo.dealer.stateProvince),
            link:
              '/locations/' +
              getStateSlug(dealerInfo.dealer.stateProvince) +
              '/',
          },
          {
            text: formatStringToDisplayCase(dealerInfo.dealer.city),
          },
        ]}
        stateSlug={getStateSlug(dealerInfo.dealer.stateProvince)}
        stateName={getFullState(dealerInfo.dealer.stateProvince)}
        headerText={formatDealerName(dealerInfo.dealer.dealerName)}
        city={formatStringToDisplayCase(dealerInfo.dealer.city)}
      />

      <div className="dealer-page constrained-width">
        <div className="dealer-info-hours">
          <div className="dealer-info">
            <span className="dealer-address">
              <LocationIcon />
              <div className="dealer-address-text">
                <p>{formatStringToDisplayCase(dealerInfo.dealer.address1)}</p>
                {dealerInfo.dealer.address2 && (
                  <p className="caption">
                    {formatStringToDisplayCase(dealerInfo.dealer.address2)}
                  </p>
                )}
                <p>
                  {formatStringToDisplayCase(dealerInfo.dealer.city)},{' '}
                  {dealerInfo.dealer.stateProvince.toUpperCase()}{' '}
                  {dealerInfo.dealer.postalCode}
                </p>
              </div>
            </span>
            <span className="dealer-phone">
              <PhoneIcon />
              <p>{formatPhone(dealerInfo.dealer.phoneNumber)}</p>
              <a href={'tel:' + dealerInfo.dealer.phoneNumber}>
                {formatPhone(dealerInfo.dealer.phoneNumber)}
              </a>
            </span>
            {dealerUrl && (
              <Button
                className="dealer-website gtm-dealer-website"
                isCompact={true}
                nextJSUrl={dealerUrl}
                linkComponent={Link}
                buttonStyle="outlined"
              >
                Visit Website
              </Button>
            )}
          </div>
          <div className="dealer-hours">
            <a
              className="caption"
              onClick={(e) => e.preventDefault() | setShowHours(!showHours)}
              href="#"
            >
              {showHours ? 'Hide' : 'Show'} Hours{' '}
              <Image
                src="/public-assets/images/chevron-dwn.svg"
                className={showHours ? 'down' : 'up'}
                alt="Chevron Down"
                width="16"
                height="16"
              />
            </a>
            <div className={`dealer-hours-list ${showHours ? 'shown' : ''}`}>
              <h6>Hours</h6>
              {getHoursDisplay()}
            </div>
          </div>
          <div className="dealer-desktop-form-container">
            {dealerInfo.dealer.brandAcronym === 'CLA'
              ? renderedRequestInfoContent.claytonRetailWebForm
              : dealerInfo.dealer.brandAcronym === 'IND'
                ? renderedRequestInfoContent.independentWebForm
                : renderedRequestInfoContent.familyOfBrandsWebForm}
          </div>
        </div>
        <div className="dealer-actions">
          {dealerUrl && (
            <Button
              isCompact={true}
              fillContainer={true}
              nextJSUrl={dealerUrl}
              linkComponent={Link}
              buttonStyle="outlined"
            >
              VISIT WEBSITE
            </Button>
          )}
          <Button
            className="mobile-form-button"
            isCompact={true}
            onClick={() => setShowContactUsFormModal(true)}
            fillContainer={true}
          >
            CONTACT US
          </Button>
        </div>

        <div className="home-listings">
          <h2>Our Homes</h2>{' '}
          <p>
            {inventoryModels.length} in stock,{' '}
            {availableModels ? availableModels.length : 0} available floor plans
          </p>
          <DXFavoritesContext.Consumer>
            {({ actions, state }) => {
              let dynamicCTACurrentCount = -1;

              return (
                dealerModels &&
                dealerModels.slice(0, modelsDisplayed).map((home, i) => {
                  let ctas;

                  if (renderedAreaContent) {
                    if (
                      dealerInfo.dealer.brandAcronym === 'CLA' &&
                      renderedAreaContent.claytonRetailPromoCtas !== null
                    ) {
                      ctas = renderedAreaContent.claytonRetailPromoCtas;
                    } else if (
                      dealerInfo.dealer.brandAcronym === 'IND' &&
                      renderedAreaContent.independentPromoCtas !== null
                    ) {
                      ctas = renderedAreaContent.independentPromoCtas;
                    } else if (
                      renderedAreaContent.familyOfBrandsPromoCtas !== null
                    ) {
                      ctas = renderedAreaContent.familyOfBrandsPromoCtas;
                    }
                  }

                  //every sixth slide, show a CTA.
                  if (ctas !== undefined) {
                    if (
                      (i % modelsPerPage === 0 &&
                        i !== 0 &&
                        dynamicCTACurrentCount < ctas.length) ||
                      (i >
                        dealerModels.length -
                          (dealerModels.length % modelsPerPage) &&
                        i < dealerModels.length - 1 &&
                        dynamicCTACurrentCount < ctas.length)
                    ) {
                      if (dynamicCTACurrentCount < ctas.length - 1) {
                        dynamicCTACurrentCount++;
                      } else {
                        //if we've shown all available CTAs, cycle back to the beginning and show them again (dealerModels.length%6)*6
                        dynamicCTACurrentCount = 0;
                      }
                    } else if (dealerModels.length <= modelsPerPage) {
                      dynamicCTACurrentCount = 0;
                    }
                  } else {
                    ctas = [];
                  }

                  return (
                    <>
                      {i % modelsPerPage === 0 &&
                        i !== 0 &&
                        dealerModels.length > modelsPerPage && (
                          <div>{ctas[dynamicCTACurrentCount]}</div>
                        )}
                      <HomeCardDetailed
                        key={'home-' + home.modelNumber}
                        nextJSLink={'/homes/' + home.modelNumber + '/'}
                        linkComponent={Link}
                        desktopOrientation="horizontal"
                        handleCtaClick={() => {
                          setShowFormModal(true);
                          setModelInfo(home);
                        }}
                        handleFavoriteClick={() =>
                          actions.toggleFavoriteModel(home.modelNumber)
                        }
                        images={home.images}
                        imageQueryString="?width=590&q=50&fm=webp"
                        isFavorited={
                          !!state.favoriteModels?.filter(
                            (faveModels) =>
                              faveModels.modelNumber === home.modelNumber
                          )[0]
                        }
                        showVirtualTour={true}
                        showFavoritingHeart={true}
                        showStockIndicator={true}
                        ctaText="Request Info"
                        homeObject={home}
                        showPricing={false}
                        onVirtualTourClick={(e) => {
                          e.preventDefault();
                          setShowVirtualTourModal(true);
                          setVirtualTourDisplayed(home);
                        }}
                      />
                    </>
                  );
                })
              );
            }}
          </DXFavoritesContext.Consumer>
          {dealerModels && modelsDisplayed < dealerModels.length ? (
            <Button
              className="gtm-load-homes"
              fillContainer={true}
              buttonStyle="outlined"
              onClick={(e) => {
                setModelsDisplayed(modelsDisplayed + modelsPerPage);
                e.target.blur();
              }}
            >
              LOAD MORE
            </Button>
          ) : null}
        </div>
      </div>
      {showFormModal && (
        <Modal
          onClose={() => {
            setShowFormModal(false);
            setModelInfo(null);
          }}
        >
          <div className="modal-form-header">
            <p className="label large">Requesting Information For</p>
            <p className="h3">
              {modelInfo !== null
                ? modelNameTransformer(modelInfo.modelDescription)
                : ''}
            </p>
          </div>
          {contentfulRequestInfoModalForm}
        </Modal>
      )}

      {showContactUsFormModal && (
        <Modal
          onClose={() => {
            setShowContactUsFormModal(false);
          }}
        >
          {contentfulContactUsMobileModalForm}
        </Modal>
      )}

      {showVirtualTourModal && virtualTourDisplayed && (
        <div className="virtual-tour-modal">
          <Modal onClose={() => setShowVirtualTourModal(false)}>
            <p className="vt-modal-specs">
              {modelNameTransformer(virtualTourDisplayed.modelDescription)} |{' '}
              {virtualTourDisplayed.beds} beds,&nbsp;
              {virtualTourDisplayed.baths} baths,&nbsp;
              {formatNumberWithCommas(virtualTourDisplayed.maxSquareFeet)} sq.
              ft.
            </p>
            <iframe
              title="Matterport 3D Tour"
              scrolling="no"
              frameBorder="0"
              allowFullScreen
              allow="xr-spatial-tracking"
              src={
                virtualTourDisplayed.virtualTourReference?.startsWith(
                  'https://'
                )
                  ? virtualTourDisplayed.virtualTourReference
                  : `https://my.matterport.com/show/?m=0${virtualTourDisplayed.virtualTourReference}`
              }
            />
          </Modal>
        </div>
      )}
    </DealerLPStyles>
  );
};

DealerLP.propTypes = {
  dealerSlug: PropTypes.any,
  ssrAvailableModels: PropTypes.array,
  ssrDealerInfo: PropTypes.any,
  ssrInventoryModels: PropTypes.array,
  ssrWebsiteAreaContent: PropTypes.any,
};

export default DealerLP;
